<template>
  <transition name="chat-share-fade">
    <section v-if="visible" class="chat-share-overlay" role="dialog" aria-modal="true" :aria-label="$t('directMessage.shareToChat')" @click.self="$emit('close')">
      <div class="chat-share-sheet">
        <header>
          <div><small>STARLIVE SHARE</small><h2>{{ $t("directMessage.shareToChat") }}</h2></div>
          <button type="button" :aria-label="$t('common.close')" @click="$emit('close')"><i class="el-icon-close" /></button>
        </header>
        <article v-if="content" class="share-content-preview">
          <img :src="content.cover_url" :alt="content.title" @error="hideBrokenImage">
          <span><small>{{ $t(`directMessage.contentTypes.${content.content_type}`) }}</small><strong>{{ content.title }}</strong><p>{{ content.description }}</p></span>
        </article>
        <label class="share-recipient-search">
          <i class="el-icon-search" aria-hidden="true" />
          <input v-model.trim="keyword" type="search" maxlength="50" :placeholder="$t('directMessage.shareSearchPlaceholder')" @input="scheduleSearch">
          <button v-if="keyword" type="button" :aria-label="$t('directMessage.shareClearSearch')" @click="clearSearch"><i class="el-icon-close" /></button>
        </label>
        <main>
          <div v-if="loading" class="share-picker-state"><i class="el-icon-loading" />{{ $t("directMessage.loadingRecipients") }}</div>
          <template v-else-if="conversations.length">
            <button v-for="item in conversations" :key="item.id" type="button" class="share-recipient" :disabled="sendingId > 0 || !item.peer.available" @click="send(item)">
              <avatar-with-frame class="share-recipient-avatar" :avatar="item.peer.avatar_url" :avatar-alt="item.peer.name" :frame-animation-url="item.peer.avatar_frame_url" :frame-preview-url="item.peer.avatar_frame_preview_url" />
              <span><strong>{{ item.peer.name }}</strong><small>{{ conversationSummary(item) }}</small></span>
              <i :class="sendingId === Number(item.id) ? 'el-icon-loading' : 'el-icon-position'" />
            </button>
            <button v-if="pagination.has_more" type="button" class="share-load-more" :disabled="loadingMore || sendingId > 0" @click="loadMore">
              <i :class="loadingMore ? 'el-icon-loading' : 'el-icon-bottom'" />{{ $t("directMessage.shareLoadMore") }}
            </button>
          </template>
          <div v-else class="share-picker-state"><i class="el-icon-chat-dot-round" /><strong>{{ $t(keyword ? "directMessage.noSearchResults" : "directMessage.noRecipients") }}</strong><p>{{ $t(keyword ? "directMessage.noSearchResultsTip" : "directMessage.noRecipientsTip") }}</p></div>
        </main>
      </div>
    </section>
  </transition>
</template>

<script>
import AvatarWithFrame from "./AvatarWithFrame.vue"
import { getDirectConversations, sendDirectMessage } from "../api/directMessage"

export default {
  name: "ChatSharePicker",
  components: { AvatarWithFrame },
  props: { visible: { type: Boolean, default: false }, content: { type: Object, default: null } },
  data() {
    return {
      conversations: [], keyword: "", loading: false, loadingMore: false, sendingId: 0,
      pagination: { page: 0, limit: 5, total: 0, has_more: false }, searchTimer: 0, requestId: 0
    }
  },
  watch: {
    visible(value) {
      if (value) this.resetAndLoad()
      else {
        this.requestId += 1
        window.clearTimeout(this.searchTimer)
        this.loading = false
        this.loadingMore = false
      }
    }
  },
  beforeDestroy() { window.clearTimeout(this.searchTimer) },
  methods: {
    hideBrokenImage(event) { event.target.style.display = "none" },
    resetAndLoad() {
      window.clearTimeout(this.searchTimer)
      this.keyword = ""
      this.conversations = []
      this.pagination = { page: 0, limit: 5, total: 0, has_more: false }
      return this.loadPage(1, false)
    },
    scheduleSearch() {
      window.clearTimeout(this.searchTimer)
      this.searchTimer = window.setTimeout(() => {
        this.conversations = []
        this.pagination = { page: 0, limit: 5, total: 0, has_more: false }
        this.loadPage(1, false)
      }, 300)
    },
    clearSearch() {
      this.keyword = ""
      window.clearTimeout(this.searchTimer)
      this.conversations = []
      this.pagination = { page: 0, limit: 5, total: 0, has_more: false }
      this.loadPage(1, false)
    },
    async loadPage(page, append) {
      if (append && this.loadingMore) return
      const requestId = ++this.requestId
      if (append) this.loadingMore = true
      else this.loading = true
      try {
        const res = await getDirectConversations({ page, limit: 5, sort: "recent", keyword: this.keyword })
        if (requestId !== this.requestId) return
        const rows = res.data.list || []
        if (append) {
          const known = new Set(this.conversations.map(item => Number(item.id)))
          this.conversations = this.conversations.concat(rows.filter(item => !known.has(Number(item.id))))
        } else {
          this.conversations = rows
        }
        this.pagination = res.data.pagination || { page, limit: 5, total: this.conversations.length, has_more: false }
      } finally {
        if (requestId === this.requestId) {
          this.loading = false
          this.loadingMore = false
        }
      }
    },
    loadMore() {
      if (!this.pagination.has_more || this.loadingMore) return
      this.loadPage(Number(this.pagination.page || 0) + 1, true)
    },
    conversationSummary(item) {
      if (item.last_message_type === "content") return this.$t("directMessage.sharedContent", { type: this.$t(`directMessage.contentTypes.${item.last_content_type}`) })
      return item.last_message || this.$t("directMessage.newChat")
    },
    async send(item) {
      if (!this.content || this.sendingId) return
      this.sendingId = Number(item.id)
      try {
        const res = await sendDirectMessage({
          conversation_id: item.id,
          client_message_id: `share-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`,
          message_type: "content",
          content_type: this.content.content_type,
          content_id: Number(this.content.id)
        })
        this.$emit("sent", { conversation: item, content: this.content, message: res.data.message })
      } finally { this.sendingId = 0 }
    }
  }
}
</script>

<style scoped>
.chat-share-overlay{position:fixed;z-index:3000;inset:0;display:flex;align-items:flex-end;justify-content:center;padding:20px 12px max(14px,env(safe-area-inset-bottom));background:rgba(2,3,6,.72);backdrop-filter:blur(10px)}.chat-share-sheet{width:min(100%,460px);max-height:min(78vh,680px);display:flex;flex-direction:column;overflow:hidden;border:1px solid rgba(255,255,255,.11);border-radius:24px;background:linear-gradient(155deg,#1b1d25,#0e0f14 70%);box-shadow:0 28px 80px rgba(0,0,0,.55);color:#fff}.chat-share-sheet>header{display:flex;align-items:center;justify-content:space-between;padding:18px 18px 13px}.chat-share-sheet>header small{color:#ff6077;font-size:8px;font-weight:900;letter-spacing:2px}.chat-share-sheet h2{margin:4px 0 0;font-size:19px}.chat-share-sheet>header button{width:36px;height:36px;border:1px solid #343741;border-radius:50%;background:#20222a;color:#fff}.share-content-preview{display:flex;gap:12px;margin:0 16px 12px;padding:10px;border:1px solid rgba(255,255,255,.08);border-radius:15px;background:rgba(255,255,255,.035)}.share-content-preview>img{width:56px;height:70px;flex:none;border-radius:10px;object-fit:cover}.share-content-preview>span{min-width:0}.share-content-preview small,.share-content-preview strong,.share-content-preview p{display:block;overflow:hidden;text-overflow:ellipsis}.share-content-preview small{color:#52ded3;font-size:8px;font-weight:800}.share-content-preview strong{margin-top:5px;white-space:nowrap;font-size:13px}.share-content-preview p{display:-webkit-box;margin:5px 0 0;color:#858993;font-size:9px;line-height:1.45;-webkit-box-orient:vertical;-webkit-line-clamp:2}.share-recipient-search{height:44px;display:flex;align-items:center;gap:8px;margin:0 16px 10px;padding:0 12px;border:1px solid #30333c;border-radius:14px;background:#15171d;color:#767b86}.share-recipient-search input{min-width:0;flex:1;border:0;outline:0;color:#fff;background:transparent;font-size:16px}.share-recipient-search input::placeholder{color:#696d77}.share-recipient-search button{width:30px;height:30px;display:grid;place-items:center;padding:0;border:0;border-radius:50%;color:#9b9faa;background:#24262e}.chat-share-sheet>main{min-height:180px;overflow-y:auto;padding:0 16px 18px;scrollbar-width:none}.chat-share-sheet>main::-webkit-scrollbar{display:none}.share-recipient{width:100%;display:flex;align-items:center;gap:12px;padding:12px 2px;border:0;border-bottom:1px solid rgba(255,255,255,.07);background:none;color:#fff;text-align:inherit}.share-recipient:disabled{opacity:.55}.share-recipient-avatar{position:relative;width:45px;height:45px;flex:none}.share-recipient-avatar>img{width:100%;height:100%;border-radius:50%;object-fit:cover}.share-recipient-frame{position:absolute;inset:-7px}.share-recipient>span:nth-child(2){min-width:0;flex:1}.share-recipient strong,.share-recipient small{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.share-recipient strong{font-size:12px}.share-recipient small{margin-top:5px;color:#777b85;font-size:9px}.share-recipient>i{color:#ff6077;font-size:17px}.share-load-more{width:100%;min-height:42px;margin-top:10px;border:1px solid #30333c;border-radius:13px;color:#c8cbd2;background:#1b1d24}.share-load-more i{margin-inline-end:7px;color:#ff6077}.share-load-more:disabled{opacity:.55}.share-picker-state{min-height:190px;display:grid;place-content:center;justify-items:center;gap:7px;color:#777b85;text-align:center;font-size:10px}.share-picker-state>i{font-size:35px;color:#353841}.share-picker-state strong{color:#fff;font-size:14px}.share-picker-state p{max-width:250px;margin:0;line-height:1.6}.chat-share-fade-enter-active,.chat-share-fade-leave-active{transition:opacity .2s ease}.chat-share-fade-enter-active .chat-share-sheet,.chat-share-fade-leave-active .chat-share-sheet{transition:transform .2s ease}.chat-share-fade-enter,.chat-share-fade-leave-to{opacity:0}.chat-share-fade-enter .chat-share-sheet,.chat-share-fade-leave-to .chat-share-sheet{transform:translateY(24px)}
</style>
